import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../components/layout';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { adminClientsService, ClientListItem } from '../../services/adminClientsService';
import { CreateClientModal } from '../../components/parametres/CreateClientModal';
import { toastError } from '../../lib/toast';

const STORAGE_LABELS: Record<string, string> = {
  minio_local: 'minio',
  s3_remote: 's3',
};

const STORAGE_BADGE: Record<string, string> = {
  minio_local: 'bg-blue-100 text-blue-800',
  s3_remote: 'bg-amber-100 text-amber-800',
};

export const ClientsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showSystem, setShowSystem] = useState(false);

  const load = async (includeSystem: boolean = showSystem) => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminClientsService.listClients({ includeSystem });
      setClients(data);
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
      toastError('Chargement des clients échoué', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(showSystem);
  }, [showSystem]);

  const formatDate = (s: string) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const columns: FilterableColumnDef<ClientListItem>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      searchable: true,
      render: (c) => (
        <span className="font-medium">
          {c.name}
          {c.is_system_tenant && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
              Système
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      sortable: true,
      searchable: true,
      render: (c) => <span className="font-mono text-sm text-gray-700">{c.slug}</span>,
    },
    {
      key: 'storage_backend',
      header: 'Storage',
      filterable: true,
      filterValueFn: (c) => c.storage_backend,
      filterOptions: [
        { value: 'minio_local', label: 'MinIO local' },
        { value: 's3_remote', label: 'S3 distant' },
      ],
      render: (c) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STORAGE_BADGE[c.storage_backend] || 'bg-gray-100 text-gray-800'}`}>
          {STORAGE_LABELS[c.storage_backend] || c.storage_backend}
        </span>
      ),
    },
    {
      key: 'extraction_policy',
      header: 'Policy',
      filterable: true,
      filterValueFn: (c) => c.extraction_policy,
      render: (c) => <span className="text-sm">{c.extraction_policy}</span>,
    },
    {
      key: 'created_at',
      header: 'Créé le',
      sortable: true,
      sortValueFn: (c) => c.created_at || '',
      render: (c) => <span className="text-sm text-gray-600">{formatDate(c.created_at)}</span>,
    },
    {
      key: 'is_active',
      header: 'Actif',
      filterable: true,
      filterValueFn: (c) => (c.is_active ? 'actif' : 'inactif'),
      filterOptions: [
        { value: 'actif', label: 'Actif' },
        { value: 'inactif', label: 'Inactif' },
      ],
      render: (c) => c.is_active ? (
        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">✓</span>
      ) : (
        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">✗</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (c) => (
        <button
          onClick={() => navigate(`/parametres/clients/${c.id}`)}
          className="text-blue-600 hover:underline text-sm"
        >
          Ouvrir
        </button>
      ),
    },
  ], [navigate]);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Clients</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showSystem}
              onChange={(e) => setShowSystem(e.target.checked)}
            />
            Afficher tenants système
          </label>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
          >
            + Nouveau client
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 py-8 text-center">Chargement...</div>
      ) : error ? (
        <div className="text-red-600 py-8 text-center">{error}</div>
      ) : (
        <FilterableTable
          persistKey="parametres-clients"
          paginated
          data={clients}
          columns={columns}
          rowKey={(c) => c.id}
          emptyMessage="Aucun client trouvé"
          noDataMessage="Aucun client"
          defaultSort={{ key: 'name', direction: 'asc' }}
        />
      )}

      <CreateClientModal isOpen={showCreate} onClose={() => { setShowCreate(false); load(); }} />
    </PageContainer>
  );
};

export default ClientsListPage;
